/* TabIncidentes.jsx — Pestaña de Incidentes en /combustible
   (Octavio 19-jun-2026 · 6ta iteración).

   Tipos:
     - TICKET_REIMPRESO: ticket de gasolinera reimpreso (folio debe existir en cargas)
     - VALE_PERDIDO:     vale extraviado (marca folio como PERDIDO en el sistema)
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import "../styles/supervisor.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTAMENTOS = [
  "ADMINISTRATIVO", "ALUMBRADO PUBLICO", "ALMACEN", "AREAS VERDES CONTRATADAS",
  "CONTROL VEHICULAR", "FONTANERIA", "FORESTACION / VIVERO", "HERRERIA",
  "MONUMENTOS", "OBRA MENOR", "PARQUES Y JARDINES / VIALIDADES", "PIPAS",
  "TRABAJOS ESPECIALES",
];

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default function TabIncidentes() {
  const [tipo, setTipo] = useState("TICKET_REIMPRESO");
  const [folio, setFolio] = useState("");
  const [chofer, setChofer] = useState("");
  const [departamento, setDepartamento] = useState("");
  const [fecha, setFecha] = useState(hoyISO);
  const [notas, setNotas] = useState("");
  const [busy, setBusy] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fTipo, setFTipo] = useState("");
  const [fDepto, setFDepto] = useState("");
  const [fFecha, setFFecha] = useState("");
  const [fStatus, setFStatus] = useState("");

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fTipo) params.tipo = fTipo;
      if (fDepto) params.departamento = fDepto;
      if (fFecha) params.fecha = fFecha;
      if (fStatus) params.status = fStatus;
      const r = await axios.get(`${API}/incidentes-combustible`, { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar incidentes");
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, [fTipo, fDepto, fFecha, fStatus]);

  const submit = async (e) => {
    e.preventDefault();
    if (!folio.trim()) return toast.error("Folio obligatorio");
    if (!chofer.trim() || chofer.trim().length < 2) return toast.error("Nombre del chofer obligatorio");
    if (tipo === "VALE_PERDIDO" && !departamento) return toast.error("Departamento obligatorio para vale extraviado");
    if (!fecha) return toast.error("Fecha obligatoria");
    setBusy(true);
    try {
      await axios.post(`${API}/incidentes-combustible`, {
        tipo, folio: folio.trim(), nombre_chofer: chofer.trim(),
        departamento, fecha, notas: notas.trim(),
      });
      toast.success("Incidente registrado");
      setFolio(""); setChofer(""); setDepartamento(""); setNotas("");
      setFecha(hoyISO());
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar");
    } finally { setBusy(false); }
  };

  const toggleStatus = async (inc) => {
    const nuevo = inc.status === "ABIERTO" ? "RESUELTO" : "ABIERTO";
    try {
      await axios.patch(`${API}/incidentes-combustible/${inc.id}`, { status: nuevo });
      toast.success(`Incidente marcado como ${nuevo}`);
      cargar();
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  return (
    <div className="space-y-4" data-testid="tab-incidentes-content">
      {/* FORMULARIO */}
      <div className="card-base p-4 bg-white">
        <h3 className="text-base font-bold text-slate-800 mb-3">
          <i className="fa-solid fa-triangle-exclamation mr-2 text-amber-600"/>Registrar incidente
        </h3>

        {/* Selector tipo */}
        <div className="flex gap-2 mb-3">
          <button type="button"
                  onClick={() => setTipo("TICKET_REIMPRESO")}
                  data-testid="inc-tipo-reimpreso"
                  className={`btn-seleccion flex-1 ${tipo === "TICKET_REIMPRESO" ? "activo" : ""}`}>
            <i className="fa-solid fa-receipt mr-2"/>Ticket de gasolina reimpreso
          </button>
          <button type="button"
                  onClick={() => setTipo("VALE_PERDIDO")}
                  data-testid="inc-tipo-perdido"
                  className={`btn-seleccion flex-1 ${tipo === "VALE_PERDIDO" ? "activo-rojo" : ""}`}>
            <i className="fa-solid fa-circle-xmark mr-2"/>Vale extraviado
          </button>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">
              {tipo === "TICKET_REIMPRESO" ? "Folio de vale (ya cargado) *" : "No. de folio del vale *"}
            </span>
            <input value={folio} onChange={(e) => setFolio(e.target.value)} required
                   data-testid="inc-inp-folio"
                   placeholder="Ej. 12345"
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1"/>
          </label>

          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Nombre del chofer que lo perdió *</span>
            <input value={chofer} onChange={(e) => setChofer(e.target.value.toUpperCase())} required
                   data-testid="inc-inp-chofer"
                   placeholder="JUAN PEREZ"
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1 uppercase"/>
          </label>

          {tipo === "VALE_PERDIDO" && (
            <label className="block">
              <span className="text-xs font-bold text-slate-600 uppercase">Departamento *</span>
              <select value={departamento} onChange={(e) => setDepartamento(e.target.value)} required
                      data-testid="inc-sel-depto"
                      className="w-full border border-slate-300 rounded px-2 py-2 mt-1">
                <option value="">— Selecciona —</option>
                {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Fecha del incidente *</span>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required
                   data-testid="inc-inp-fecha"
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1"/>
          </label>

          <label className="block md:col-span-2">
            <span className="text-xs font-bold text-slate-600 uppercase">Notas (opcional)</span>
            <textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2}
                      data-testid="inc-inp-notas"
                      placeholder="Detalles adicionales…"
                      className="w-full border border-slate-300 rounded px-2 py-2 mt-1"/>
          </label>

          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={busy}
                    data-testid="inc-btn-submit"
                    className="bg-amber-600 hover:bg-amber-700 text-white font-bold rounded px-4 py-2 disabled:opacity-60">
              <i className="fa-solid fa-paper-plane mr-1"/>
              {busy ? "Guardando…" : "Registrar incidente"}
            </button>
          </div>
        </form>

        {tipo === "VALE_PERDIDO" && (
          <div className="mt-2 text-xs text-slate-500 italic">
            <i className="fa-solid fa-circle-info mr-1"/>
            Este folio quedará marcado como PERDIDO (no bloquea operación; reactivable manualmente).
          </div>
        )}
        {tipo === "TICKET_REIMPRESO" && (
          <div className="mt-2 text-xs text-slate-500 italic">
            <i className="fa-solid fa-circle-info mr-1"/>
            Solo registro informativo. NO afecta la carga ya guardada del folio.
          </div>
        )}
      </div>

      {/* TABLA */}
      <div className="card-base p-4 bg-white">
        <h3 className="text-base font-bold text-slate-800 mb-3">
          <i className="fa-solid fa-list mr-2 text-slate-500"/>Historial de incidentes
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <select value={fTipo} onChange={(e) => setFTipo(e.target.value)}
                  data-testid="inc-flt-tipo"
                  className="border border-slate-300 rounded px-2 py-2 text-sm">
            <option value="">Todos los tipos</option>
            <option value="TICKET_REIMPRESO">Ticket reimpreso</option>
            <option value="VALE_PERDIDO">Vale extraviado</option>
          </select>
          <select value={fDepto} onChange={(e) => setFDepto(e.target.value)}
                  data-testid="inc-flt-depto"
                  className="border border-slate-300 rounded px-2 py-2 text-sm">
            <option value="">Todos los departamentos</option>
            {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <input type="date" value={fFecha} onChange={(e) => setFFecha(e.target.value)}
                 data-testid="inc-flt-fecha"
                 className="border border-slate-300 rounded px-2 py-2 text-sm"/>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
                  data-testid="inc-flt-status"
                  className="border border-slate-300 rounded px-2 py-2 text-sm">
            <option value="">Todos los status</option>
            <option value="ABIERTO">Abierto</option>
            <option value="RESUELTO">Resuelto</option>
          </select>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-4">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 py-4" data-testid="inc-empty">Sin incidentes registrados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>
                  <th className="px-2 py-2 text-left">Fecha</th>
                  <th className="px-2 py-2 text-left">Tipo</th>
                  <th className="px-2 py-2 text-left">Folio</th>
                  <th className="px-2 py-2 text-left">Chofer</th>
                  <th className="px-2 py-2 text-left">Departamento</th>
                  <th className="px-2 py-2 text-center">Status</th>
                  <th className="px-2 py-2 text-left">Notas</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className="border-t border-slate-100" data-testid={`inc-row-${i.id}`}>
                    <td className="px-2 py-2">{i.fecha}</td>
                    <td className="px-2 py-2">
                      {i.tipo === "TICKET_REIMPRESO"
                        ? <span style={{ background: "#DBEAFE", color: "#1E40AF", padding: "2px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>Ticket reimpreso</span>
                        : <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "2px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>Vale extraviado</span>}
                    </td>
                    <td className="px-2 py-2 font-mono">{i.folio}</td>
                    <td className="px-2 py-2">{i.nombre_chofer}</td>
                    <td className="px-2 py-2">{i.departamento || "—"}</td>
                    <td className="px-2 py-2 text-center">
                      <button type="button"
                              onClick={() => toggleStatus(i)}
                              data-testid={`inc-toggle-${i.id}`}
                              title="Click para alternar"
                              style={{
                                background: i.status === "ABIERTO" ? "#FEF3C7" : "#DCFCE7",
                                color: i.status === "ABIERTO" ? "#92400E" : "#14532D",
                                padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                                border: 0, cursor: "pointer",
                              }}>
                        {i.status}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-slate-600 max-w-xs truncate" title={i.notas}>{i.notas || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
